import { trackR2BReferralsVisitsToLandingPageWithBonusReferralOfferView } from "@faire/web-api/events/r2BReferrals/view/visitsToLandingPageWithBonusReferralOffer";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Spacer } from "@faire/web/slate/spacing";
import { useState } from "@faire/web/ui/hooks/useState";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import styled from "styled-components";

import { FooterSEOContent } from "@faire/retailer/components/Footer/FooterSEOContent";
import { GeographicMap } from "@faire/retailer/components/GeographicMap";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

import { BrandsLandingPageCaseStudies } from "../BrandsLandingPageCaseStudies";
import { BrandsLandingPageFaireMarketBanner } from "../BrandsLandingPageFaireMarketBanner";
import { BrandsLandingPageIntro } from "../BrandsLandingPageImprovementsIntro";
import { BrandsLandingPageTestimonials } from "../BrandsLandingPageTestimonials";
import { BrandsLandingPageTestimonialsSwipe } from "../BrandsLandingPageTestimonialsSwipe";
import {
  BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE,
  getIsShowingCaseStudies,
} from "../const";
import { getIntros } from "../intros";

import { CalendlySection } from "./CalendlySection";
import { Header } from "./Header";
import { TopSection } from "./TopSection";

const FAIRE_MARKET_SECTION = "faire-market-section";

const LandingPageBase: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();
  const [showCTA, setShowCTA] = useState(false);

  const { showBrandLandingPage } = useMarketStore(["showBrandLandingPage"]);

  useEffect(() => {
    trackR2BReferralsVisitsToLandingPageWithBonusReferralOfferView();
  }, []);

  return (
    <>
      {showBrandLandingPage && !showCTA ? (
        <BrandsLandingPageFaireMarketBanner
          faireMarketSectionId={FAIRE_MARKET_SECTION}
        />
      ) : null}

      <Header showCTA={showCTA} setShowCTA={setShowCTA} />

      <Container>
        <TopSection />
        <ResponsiveContentV2
          mobile={
            <MobileBannerImage
              src={optimizeImageUrl(
                { url: BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE },
                isSSR
              )}
            />
          }
        />
        <Spacer
          height={{
            mobileAndAbove: "3x",
            desktopAndAbove: 96,
          }}
        />

        {getIntros(isSSR, strictLocalize).map((intro) => (
          <div key={intro.index}>
            <BrandsLandingPageIntro
              index={intro.index}
              header={intro.header}
              description={intro.description}
              richContent={intro.richContent}
              reverse={intro.reverse}
              startTracking={showCTA}
              eventKey={intro.eventKey}
            />
            <Spacer
              height={{
                mobileAndAbove: "3x",
                tabletAndAbove: "6x",
              }}
            />
          </div>
        ))}

        <Spacer
          height={{
            mobileAndAbove: "1x",
            tabletAndAbove: "4x",
            desktopAndAbove: 96,
          }}
        />
        <CalendlySection />

        {getIsShowingCaseStudies() ? <BrandsLandingPageCaseStudies /> : null}

        <ResponsiveContentV2 mobile={<BrandsLandingPageTestimonialsSwipe />} />
        <GeographicMap showCaseStudies={getIsShowingCaseStudies()} />

        <BrandsLandingPageTestimonials />

        <FooterSEOContent />
      </Container>
    </>
  );
};

export const LandingPage = observer(LandingPageBase);

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  align-items: center;
  position: relative;
  overflow-x: hidden;
`;

const MobileBannerImage = styled.img`
  height: 112px;
  width: 100%;
  object-fit: cover;
  object-position: center;
`;
